import { assertNever } from "@impero-com/common/utils/obj";

import {
    NotificationRecipientKind,
    UserOverview,
    UserGroupView,
} from "api/generated/model";

import { UserOrUserGroupOverviewEnum } from "./types";

export function fromNotificationRecipientKind(
    recipient: NotificationRecipientKind,
    users: UserOverview[],
    userGroups: UserGroupView[]
): UserOrUserGroupOverviewEnum | null {
    if (!recipient) {
        return null;
    }
    switch (recipient.type) {
        case "User": {
            const user = users?.find((u) => u.id === recipient.userId);
            return user ? { type: "User", user } : null;
        }
        case "Group": {
            const userGroup = userGroups.find(
                (g) => g.id === recipient.userGroupId
            );
            return userGroup ? { type: "UserGroup", userGroup } : null;
        }
        default:
            return assertNever(recipient);
    }
}

export function toNotificationRecipientKind(
    recipientsKind: UserOrUserGroupOverviewEnum
): NotificationRecipientKind {
    switch (recipientsKind.type) {
        case "User":
            return {
                type: "User",
                userId: recipientsKind.user.id,
            };
        case "UserGroup":
            return {
                type: "Group",
                userGroupId: recipientsKind.userGroup.id,
            };
        default:
            return assertNever(recipientsKind);
    }
}

export function deduplicateRecipients(
    recipients: Array<UserOrUserGroupOverviewEnum>
): Array<UserOrUserGroupOverviewEnum> {
    const seenUserIds = new Set<number>();
    const seenGroupIds = new Set<number>();

    const isUniqueRecipient = (
        recipient: UserOrUserGroupOverviewEnum
    ): boolean => {
        switch (recipient.type) {
            case "User":
                if (seenUserIds.has(recipient.user.id)) {
                    return false;
                }
                seenUserIds.add(recipient.user.id);
                return true;
            case "UserGroup":
                if (seenGroupIds.has(recipient.userGroup.id)) {
                    return false;
                }
                seenGroupIds.add(recipient.userGroup.id);
                return true;
            default:
                return assertNever(recipient);
        }
    };

    return recipients.filter(isUniqueRecipient);
}

export function isSameRecipient(
    a: NotificationRecipientKind,
    b: NotificationRecipientKind
): boolean {
    switch (a.type) {
        case "User":
            return b.type === "User" && a.userId === b.userId;
        case "Group":
            return b.type === "Group" && a.userGroupId === b.userGroupId;
        default:
            return assertNever(a);
    }
}

export function isRecipientAlreadyPresent(
    recipients: UserOrUserGroupOverviewEnum[],
    candidate: UserOrUserGroupOverviewEnum
) {
    switch (candidate.type) {
        case "User":
            return recipients.some(
                (r) => r.type === "User" && r.user.id === candidate.user.id
            );
        case "UserGroup":
            return recipients.some(
                (r) =>
                    r.type === "UserGroup" &&
                    r.userGroup.id === candidate.userGroup.id
            );
        default:
            return assertNever(candidate);
    }
}
